import { cn } from '@morpho-org/ui-v6/utils'
import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'

/**
 * InfoItem variants using cva for Tailwind class management.
 *
 * Matches v5 InfoItem behavior:
 * - Layout: vertical flex column
 * - Gap: 12px (gap-sm) between title and content
 */
const infoItemVariants = cva('gap-sm flex flex-col')

export interface InfoItemProps
  extends HTMLAttributes<HTMLDivElement>, VariantProps<typeof infoItemVariants> {
  /** Label displayed at the top in small secondary text */
  title: string
  /** Optional text content displayed below the title */
  description?: string
  /** Optional custom content rendered below the title */
  children?: ReactNode
}

/**
 * InfoItem component - Tailwind replica of dapp-ui-v5 InfoItem
 *
 * Displays a title label with optional description text or custom children below.
 *
 * @example
 * ```tsx
 * // With description
 * <InfoItem title="Market" description="USDC/WETH" />
 *
 * // With custom children
 * <InfoItem title="Owner">
 *   <TextAddress address="0x..." />
 * </InfoItem>
 * ```
 */
const InfoItem = forwardRef<HTMLDivElement, InfoItemProps>(
  ({ className, title, description, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn(infoItemVariants(), className)} {...props}>
        <span className="text-secondary-foreground truncate text-xs">{title}</span>
        <div className="gap-2xs flex items-center">
          {description ? <span className="text-md w-full truncate">{description}</span> : null}
          {children}
        </div>
      </div>
    )
  }
)
InfoItem.displayName = 'InfoItem'

export { InfoItem, infoItemVariants }
